import { Component, OnDestroy, OnInit } from '@angular/core';

import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { CanComponentDeactivate } from 'src/app/shared/guards/pending-changes.guard';
import { SubmissionsListViewComponent } from '../submissions-list-view/submissions-list-view.component';

import { panelSlider } from 'src/app/shared/content-panels/animations';
import { Pagination } from 'src/app/shared/models';
import { SubmissionsService } from '../services/submissions.service';
import { SubmissionView } from '../models/submission-view.model';
import {
  ActiveFiltersComponent,
  FilterService,
} from 'src/app/shared/filtering';
import { SubmissionsDetailViewComponent } from '../submissions-detail-view/submissions-detail-view.component';
import { Sort } from '@angular/material/sort';
import { DialogModel } from 'src/app/shared/models/dialog.model';
import { SubmissionConstants } from '../models/constants/submissions-constants';
import { DialogService } from 'src/app/shared/services/dialog.service';

@Component({
  selector: 'app-energy-credits-view-container',
  imports: [
    ActiveFiltersComponent,
    SubmissionsListViewComponent,
    SubmissionsDetailViewComponent,
  ],
  templateUrl: './energy-credits-view-container.component.html',
  styleUrl: './energy-credits-view-container.component.scss',
  animations: [panelSlider],
})
export class EnergyCreditsViewContainerComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  isDetailOpen = false;
  hasUnsavedChanges = false;
  submission: any;
  shouldClearSearch: boolean = false;

  //unsaved Changes Dialog Data
  dialogData: DialogModel;
  dialogTitle: string = this.submissionsConst.subDtlDialogTitle;
  dialogMessage: string = this.submissionsConst.subDtlDialogMessage;
  dialogCancelButtonText: string = this.submissionsConst.buttonTxtCancel;
  dialogConfirmButtonText: string = this.submissionsConst.buttonTxtConfirm;
  manSubmissionParams: BehaviorSubject<any> = new BehaviorSubject(null);

  get materialPageIndex(): number {
    return this._pagination.materialPageIndex;
  }

  private _subscriptions: Subscription[] = [];
  private currentSearch: { field: string; value: string } | null = null;
  private _pagination: Pagination = {
    paging: true,
    materialPageIndex: 0,
    pageNumber: 1,
    pageSize: 20,
    sortBy: 'SoldDate',
    sortDirection: 'desc',
  };

  constructor(
    private filterSvc: FilterService,
    private route: ActivatedRoute,
    private router: Router,
    private submissionsSrv: SubmissionsService,
    private dialogService: DialogService,
    private submissionsConst: SubmissionConstants,
  ) {}

  ngOnDestroy(): void {
    localStorage.removeItem('selectedSubmission');
    this._subscriptions.forEach((i) => i.unsubscribe());
  }

  ngOnInit(): void {
    //remove id in url if there is a refresh
    this.closeDetail();
    localStorage.removeItem('selectedSubmission');
    this.submissionsSrv.getSubmissions(this._pagination);
    this.filterSvc.initialize('ec_submissions_filters');

    let sub = this.filterSvc.activeFilterChanges$.subscribe((i) => {
      this.resetPagination();
      this.getSubmissions();
      this.scrollToTop();
    });
    this._subscriptions.push(sub);
  }

  onPageSort(event: Sort) {
    if (this._pagination.sortBy === event.active && event.direction === '') {
      this._pagination.sortBy = 'SoldDate';
      this._pagination.sortDirection = 'desc';
    } else {
      this._pagination.sortBy = event.active;
      this._pagination.sortDirection = event.direction;
    }

    this.getSubmissions();
  }

  onSearchChange(searchEvent: { field: string; value: string } | null) {
    const hadSearch = this.currentSearch !== null;
    this.currentSearch = searchEvent;

    if (searchEvent !== null || hadSearch) {
      this.resetPagination();
    }

    this.getSubmissions();
    this.scrollToTop();
  }

  onPageChange(event: PageEvent) {
    this._pagination.materialPageIndex = event.pageIndex;
    this._pagination.pageNumber = event.pageIndex + 1;
    this._pagination.pageSize = event.pageSize;
    this.getSubmissions();
    this.scrollToTop();
  }

  closeDetail() {
    this.isDetailOpen = false;
    this.hasUnsavedChanges = false;
    this.submission = null;
    let queryParams: Params = { id: null };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge', // remove to replace all query params by provided
    });
    if (this.manSubmissionParams.getValue()) {
      this.getSubmissions();
    }
  }

  selectSubmission(submission: SubmissionView): void {
    if (this.isDetailOpen && this.hasUnsavedChanges) {
      this.openDialog(submission);
      return;
    }
    let firstID = this.router.url.split('=')[1];

    if (!firstID || firstID === submission.Id.toString()) {
      localStorage.setItem('selectedSubmission', JSON.stringify(submission));
      this.getSelectedSubmission(submission);
    } else {
      let activeSub = JSON.parse(
        localStorage.getItem('selectedSubmission') || '{}',
      );
      let status = activeSub.Status;
      if (status === this.submissionsConst.statusReadyToSubmit) {
        this.openDialog(submission);
      } else {
        this.getSelectedSubmission(submission);
      }
    }
  }

  getSelectedSubmission(submission: SubmissionView): void {
    let queryParams: Params = { id: this.submission?.Id };
    this.submission = submission;
    if (this.submission) {
      this.isDetailOpen = true;
      queryParams = { id: this.submission?.Id };
      this.submissionsSrv.getSubmission(submission.Id);

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge', // remove to replace all query params by provided
      });
    } else {
      this.manSubmissionParams.next(null);
      this.closeDetail();
    }
  }

  updateView(): void {
    this.getSubmissions();
  }

  /***
   * opens the dialog to warn user of unsaved changes on 'ReadyToSubmit' status
   * when clicking on a new submission in the list
   * will reset form if user clicks 'Confirm'
   * will do nothing if user clicks 'Cancel'
   **/
  canDeactivate(): Observable<boolean> | boolean {
    if (this.isDetailOpen && this.hasUnsavedChanges) {
      const dialogRef = this.dialogService.openDialog(
        this.dialogTitle,
        this.dialogMessage,
        this.dialogCancelButtonText,
        this.dialogConfirmButtonText,
        true,
      );
      return dialogRef.afterClosed().pipe(map((result) => result === true));
    }
    return true;
  }

  openDialog(submission: SubmissionView) {
    const dialogRef = this.dialogService.openDialog(
      this.dialogTitle,
      this.dialogMessage,
      this.dialogCancelButtonText,
      this.dialogConfirmButtonText,
      true,
    );

    //get response when user confirms and reset the form
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        localStorage.removeItem('selectedSubmission');
        this.getSelectedSubmission(submission);
      } else {
        //do nothing
      }
    });
  }

  private resetPagination(): void {
    this._pagination.materialPageIndex = 0;
    this._pagination.pageNumber = 1;
  }

  // Calls getSubmissions with the current filters, search param, and pagination (includes sorting).
  private getSubmissions() {
    const params = this.getSubParams();
    this.submissionsSrv.getSubmissions(params);
  }

  getSubParams() {
    if (this.manSubmissionParams.getValue()) {
      const prevParams = this.manSubmissionParams.getValue();
      this.manSubmissionParams.next(null);
      return prevParams;
    }
    let submissionParams = this.filterSvc.activeFilters
      ? {
          ...this._pagination,
          ...this.filterSvc.convertToQueryFilter(this.filterSvc.activeFilters),
        }
      : this._pagination;

    if (this.currentSearch) {
      submissionParams = {
        ...submissionParams,
        searchField: this.currentSearch.field,
        searchValue: this.currentSearch.value,
        IsSearchMode: true,
      };
    }
    return submissionParams;
  }

  onManualSubNavigation(submission: SubmissionView) {
    this.manSubmissionParams.next(this.getSubParams());
    this.getSelectedSubmission(submission);
  }

  private scrollToTop(): void {
    setTimeout(() => {
      const panelBody = document.getElementById('submissions-panel-body');
      if (panelBody) {
        panelBody.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }, 100);
  }

  resetView(): void {
    localStorage.removeItem('selectedSubmission');
    this.closeDetail();
    this.filterSvc.removeAllActiveFilters();
    this.shouldClearSearch = true;
  }
}
